import { requestCache } from './requestCache';

interface User {
  id: string;
  username: string;
  role: string;
  tenant_id?: string;
  loginId?: string;
  loginIdLastChanged?: string;
}

interface CacheOptions {
  useCache?: boolean;
  ttl?: number; // キャッシュ保持時間（ミリ秒）
}

interface InvalidateCacheOptions {
  invalidateCache?: string | string[]; // 無効化するキャッシュキーのパターン
}

interface Tenant {
  id: string;
  name: string;
  invite_code: string;
  is_active: boolean;
}

interface LoginResponse {
  token: string;
  user: User;
}

class AuthService {
  private readonly TOKEN_KEY = 'mitoite_token';
  private readonly API_BASE_URL = this.getApiUrl();

  async login(loginCode: string, loginId?: string): Promise<User> {
    const response = await fetch(`${this.API_BASE_URL}/api/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        loginCode,
        loginId: loginId || undefined,
      }),
    });

    if (!response.ok) {
      throw new Error('ログインに失敗しました');
    }

    const data: LoginResponse = await response.json();
    localStorage.setItem(this.TOKEN_KEY, data.token);
    return data.user;
  }

  getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) return false;

    try {
      // JWT のペイロード部分を取得
      const base64Payload = token.split('.')[1];

      // Base64 URL デコード（UTF-8対応）
      const padding = base64Payload.length % 4;
      const paddedBase64 =
        base64Payload + '='.repeat(padding ? 4 - padding : 0);
      const decodedBytes = atob(
        paddedBase64.replace(/-/g, '+').replace(/_/g, '/')
      );

      // UTF-8 文字列として正しくデコード
      const utf8String = decodeURIComponent(escape(decodedBytes));
      const payload = JSON.parse(utf8String);

      return payload.exp > Date.now() / 1000;
    } catch {
      return false;
    }
  }

  private getApiUrl(): string {
    // 本番環境では環境変数から、開発環境では localhost
    if (import.meta.env.PROD) {
      return import.meta.env.VITE_API_URL || 'https://mitoite.yag13s.link';
    }
    return import.meta.env.VITE_API_URL || 'http://localhost:8080';
  }

  async authFetch(
    url: string,
    options: RequestInit & CacheOptions & InvalidateCacheOptions = {}
  ): Promise<Response> {
    const {
      useCache = false,
      ttl = 5 * 60 * 1000,
      invalidateCache,
      ...fetchOptions
    } = options;

    // キャッシュ無効化処理
    if (invalidateCache) {
      const patterns = Array.isArray(invalidateCache)
        ? invalidateCache
        : [invalidateCache];
      patterns.forEach(pattern => {
        requestCache.clear(pattern);
      });
    }

    const token = this.getToken();
    const user = this.getCurrentUser();

    if (!token) {
      throw new Error('認証が必要です');
    }

    const headers: Record<string, string> = {
      ...(fetchOptions.headers as Record<string, string>),
      Authorization: `Bearer ${token}`,
    };

    // マルチテナント対応：X-Tenant-IDヘッダーを追加
    if (user?.tenant_id) {
      headers['X-Tenant-ID'] = user.tenant_id;
    }

    // 相対URLの場合はAPIベースURLを追加
    const fullUrl = url.startsWith('http')
      ? url
      : `${this.API_BASE_URL}/api${url}`;

    // GETリクエストのみキャッシュ対象
    const method = (fetchOptions.method || 'GET').toUpperCase();
    const shouldCache = useCache && method === 'GET' && !fetchOptions.body;

    if (shouldCache) {
      // キャッシュキーを生成（URL + テナントID）
      const cacheKey = `${fullUrl}#${user?.tenant_id || 'no-tenant'}`;

      // キャッシュから取得を試行
      const cachedData = requestCache.get(cacheKey);
      if (cachedData) {
        // キャッシュされたデータから新しいResponseオブジェクトを作成
        // 重要：毎回新しいResponseを作成することでstream already readエラーを回避
        return new Response(JSON.stringify(cachedData.body), {
          status: cachedData.status,
          statusText: cachedData.statusText,
          headers: new Headers({
            'Content-Type': 'application/json',
            ...cachedData.headers,
          }),
        });
      }

      // 進行中のリクエストがある場合はそれを待つ
      const pendingRequest = requestCache.getPendingRequest(cacheKey);
      if (pendingRequest) {
        // pendingRequestの結果も新しいResponseオブジェクトとして返す
        const cachedResponse = await pendingRequest;
        if (cachedResponse) {
          const cachedDataAgain = requestCache.get(cacheKey);
          if (cachedDataAgain) {
            return new Response(JSON.stringify(cachedDataAgain.body), {
              status: cachedDataAgain.status,
              statusText: cachedDataAgain.statusText,
              headers: new Headers({
                'Content-Type': 'application/json',
                ...cachedDataAgain.headers,
              }),
            });
          }
        }
        return cachedResponse;
      }

      // 新しいリクエストを実行
      const requestPromise = this.executeRequest(
        fullUrl,
        fetchOptions,
        headers,
        cacheKey,
        ttl
      );
      requestCache.setPendingRequest(cacheKey, requestPromise);

      return requestPromise;
    }

    // キャッシュを使わない場合は通常のリクエスト
    return this.executeRequest(fullUrl, fetchOptions, headers);
  }

  private async executeRequest(
    fullUrl: string,
    fetchOptions: RequestInit,
    headers: Record<string, string>,
    cacheKey?: string,
    ttl?: number
  ): Promise<Response> {
    const response = await fetch(fullUrl, {
      ...fetchOptions,
      headers,
    });

    // 401エラーの場合は自動ログアウト
    if (response.status === 401) {
      this.logout();
      throw new Error('認証が切れました。再度ログインしてください。');
    }

    // キャッシュする場合はレスポンスをクローンして保存
    if (cacheKey && response.ok) {
      try {
        const clonedResponse = response.clone();
        const body = await clonedResponse.json();

        const cacheData = {
          body,
          status: response.status,
          statusText: response.statusText,
          headers: Object.fromEntries(response.headers.entries()),
        };

        requestCache.set(cacheKey, cacheData, ttl);
      } catch (error) {
        // JSONパースエラーは無視（画像などの非JSONレスポンス）
        console.debug('Failed to cache response (not JSON):', error);
      }
    }

    return response;
  }

  async getTenantInfo(): Promise<Tenant | null> {
    const user = this.getCurrentUser();
    if (!user?.tenant_id) return null;

    try {
      const response = await this.authFetch('/tenant', {
        useCache: true,
        ttl: 10 * 60 * 1000, // 10分間キャッシュ
      });
      if (!response.ok) {
        throw new Error('テナント情報の取得に失敗しました');
      }
      return await response.json();
    } catch (error) {
      console.error('Failed to fetch tenant info:', error);
      return null;
    }
  }

  // ローカルストレージのユーザー情報を更新
  updateLocalUser(updatedUser: Partial<User>): User | null {
    const currentUser = this.getCurrentUser();
    if (!currentUser) return null;

    const newUser = { ...currentUser, ...updatedUser };

    // JWTトークンは変更せず、ローカルストレージに一時的にユーザー情報を保存
    // 次回ログイン時に正しい情報で上書きされる
    localStorage.setItem('user_profile', JSON.stringify(newUser));

    return newUser;
  }

  // getCurrentUserメソッドを拡張してローカル更新情報も考慮
  getCurrentUser(): User | null {
    // まずローカルに保存された更新情報を確認
    const localProfile = localStorage.getItem('user_profile');
    if (localProfile) {
      try {
        return JSON.parse(localProfile);
      } catch {
        // パースエラーの場合は削除してJWTから取得
        localStorage.removeItem('user_profile');
      }
    }

    // JWTから取得（既存の処理）
    const token = this.getToken();
    if (!token || !this.isAuthenticated()) return null;

    try {
      // JWT のペイロード部分を取得
      const base64Payload = token.split('.')[1];

      // Base64 URL デコード（UTF-8対応）
      const padding = base64Payload.length % 4;
      const paddedBase64 =
        base64Payload + '='.repeat(padding ? 4 - padding : 0);
      const decodedBytes = atob(
        paddedBase64.replace(/-/g, '+').replace(/_/g, '/')
      );

      // UTF-8 文字列として正しくデコード
      const utf8String = decodeURIComponent(escape(decodedBytes));
      const payload = JSON.parse(utf8String);

      return {
        id: payload.user_id,
        username: payload.username,
        role: payload.role,
        tenant_id: payload.tenant_id,
      };
    } catch {
      return null;
    }
  }

  // プロフィール情報を取得
  async getProfile(): Promise<User> {
    const response = await this.authFetch('/profile');
    if (!response.ok) {
      throw new Error('プロフィール情報の取得に失敗しました');
    }
    return await response.json();
  }

  // ログインIDを設定
  async setLoginId(
    loginId: string
  ): Promise<{ message: string; loginId: string }> {
    const response = await this.authFetch('/profile/login-id', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ loginId }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'ログインIDの設定に失敗しました');
    }

    return await response.json();
  }

  // ログインIDの可用性をチェック
  async checkLoginIdAvailability(
    loginId: string
  ): Promise<{ available: boolean; loginId: string; error?: string }> {
    const response = await this.authFetch(
      `/profile/login-id/check?loginId=${encodeURIComponent(loginId)}`
    );

    if (!response.ok) {
      if (response.status === 400) {
        const error = await response.json();
        return { available: false, loginId, error: error.error };
      }
      throw new Error('ログインIDの確認に失敗しました');
    }

    return await response.json();
  }

  // ログインIDのバリデーション（クライアント側）
  validateLoginId(loginId: string): string | null {
    if (!loginId) return null; // 空は許可

    if (loginId.length < 3 || loginId.length > 20) {
      return 'ログインIDは3文字以上20文字以下で入力してください';
    }

    if (!/^[a-zA-Z][a-zA-Z0-9_]*$/.test(loginId)) {
      return 'ログインIDは英字で始まり、英数字とアンダースコアのみ使用可能です';
    }

    const reservedWords = [
      'admin',
      'root',
      'user',
      'guest',
      'anonymous',
      'system',
      'api',
      'www',
      'mail',
      'ftp',
    ];
    if (reservedWords.includes(loginId.toLowerCase())) {
      return 'このログインIDは使用できません';
    }

    return null;
  }

  // ログアウト時にローカルプロフィール情報もクリア
  logout(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem('user_profile');
  }

  // キャッシュ無効化メソッド
  invalidateCache(pattern?: string): void {
    if (pattern) {
      requestCache.clear(pattern);
    } else {
      // 全キャッシュクリア
      requestCache.clear();
    }
  }

  // 写真関連キャッシュの無効化
  invalidatePhotoCache(): void {
    requestCache.clear('/api/photos');
    requestCache.clear('/photos/');
  }

  // コメント関連キャッシュの無効化
  invalidateCommentCache(photoId?: string): void {
    if (photoId) {
      requestCache.clear(`/photos/${photoId}/comments`);
    } else {
      requestCache.clear('/comments');
    }
  }
}

export const authService = new AuthService();
export type { User, Tenant };
